import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from "prop-types";
import { Accordion, Button, Divider, Form, Modal } from 'semantic-ui-react';
import { useTextResources, useMessageResources } from '../../language';
import { isMobileOnly } from 'mobile-device-detect';
import FoliosDropDown from '../shared/FoliosDropDown';
import { EntityType } from '../shared/constants';
import CurrencyDropDown from '../shared/CurrencyDropDown';
import CurrencyInput from '../shared/CurrencyInput';
import { Currencies } from '../shared/currencies';
import TransactionsDropDown from '../shared/TransactionsDropDown';
import SavedCardsList from '../Payment/savedCardsList';
import { GetReservationTotals, RenderTemplateToPdf } from '../../api/services/pms';
import { onError, onSuccess, onWarning } from '../shared/messages';
import BanksDropDown from '../shared/BanksDropDown';
import { DatePicker } from '../shared/DatePicker';
import ExternalPayments from '../ExternalPaymentsComponent';
import { useLoader } from '../shared/utils';
import { CreditCardAPIPayment, InsertTransactions, SendEmail } from '../../api/services/finance';
import { GetPinPadCode } from '../Payment/paymentCommon'
import { PermissionsActions, UserHasPermission } from '../shared/permissionsActions';
import { saveAs } from 'file-saver';


const ReceiptPopup = ({ onClose, reservation: initialReservation, controller, startOpen, isDefaultSendReceiptByMail, ...rest }) => {
    const [reservation, setReservation] = useState();
    const [open, setOpen] = useState(startOpen);
    const text = useTextResources()
    const message = useMessageResources()
    const [currency, setCurrency] = useState(0)
    const [amount, setAmount] = useState(0)

    const handleAmountChange = (newAmount) => {
        // Check if user is trying to enter a negative amount (refund)
        if (newAmount < 0) {
            onWarning(message("RefundNotPermittedForLoggedInUser"));
            return;
        }
        setAmount(newAmount)
    }
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState()
    const [paymentTypeId, setPaymentTypeId] = useState()
    const [folio, setFolio] = useState()
    const cvvInputRef = useRef()
    const loader = useLoader()

    useEffect(() => {
        if (controller) {
            window[controller] = async (action,  params) => {
                switch (action) {
                    case "open":
                        if(params.reservation)
                            setReservation(params.reservation)
                        setOpen(true)
                        break;
                    default:
                        break;
                }
            }
        }
    }, [])

    useEffect(() => {
        if(initialReservation){
            setReservation(initialReservation);
            setCurrency(initialReservation.CurrencyId);
        }
    },[initialReservation])

    useEffect(() => {
        setOpen(startOpen)
    }, [startOpen])

    useEffect(() => {
        if(!reservation) return;

        setCurrency(reservation.CurrencyId)
        setSelectedPaymentMethod(undefined)
        setPaymentTypeId(undefined)
    }, [reservation, open])


    useEffect(() => {
        if (!reservation) return;
        (async () => {
            try {
                let totals = await GetReservationTotals({
                    ReservationIDs: reservation.Id,
                    HotelId: reservation.HotelId,
                    TargetCurrencyID: currency
                })
                totals = totals[0]
                setAmount(totals.TotalAmountDebitAfterTax + totals.TotalAmountCreditAfterTax)
            } catch (error) {
                onError(error)
            }
        })()
    }, [reservation, open])

    const close = () => {
        setOpen(false)
        if (onClose) {
            onClose()
        }
    }

    const save = async () => {
        try {
            //disable closing the window while transaction in proccess
            window.onbeforeunload = event => {
                event.preventDefault();
                return ''
            };
            //show loader
            loader(true);
            //generate request depending on the input
            //go to the server and do the payment
            let checksDetails = []
            if (selectedPaymentMethod.ChecksDetails) {
                checksDetails.push({
                    ID: selectedPaymentMethod.ChecksDetails.Id,
                    BankID: selectedPaymentMethod.ChecksDetails.BankID,
                    BranchCode: selectedPaymentMethod.ChecksDetails.BranchCode,
                    CheckNumber: selectedPaymentMethod.ChecksDetails.CheckNumber,
                    CheckDate: selectedPaymentMethod.ChecksDetails.CheckDate,
                    AccountNumber: selectedPaymentMethod.ChecksDetails.AccountNumber,
                    FolioTransactionID: selectedPaymentMethod.ChecksDetails.FolioTransactionID
                })
            }
            let result;
            if (selectedPaymentMethod.transaction) {
                result = await InsertTransactions([{
                    ...selectedPaymentMethod.transaction,
                    ChecksDetails:checksDetails, 
                    Amount: -amount,
                    AmountBeforeTax: -amount,
                    AmountAfterTax: -amount,
                    FolioId: folio.Id,
                    CurrencyId: currency
                }])
            } else {
                const request = GenerateRequestFromSelectedPaymentMethod();
                result = await CreditCardAPIPayment(request);
                result.PathOriginal && saveAs(result.PathOriginal);
            }
            //handle closing loader and retrive response based on the error
            if (result.ErrorText) {
                onError(result.ErrorText);
            } else {

                if(isDefaultSendReceiptByMail){
                    let justCreatedTransaction = result[0];
                    sendReceipt(justCreatedTransaction);
                }
                onSuccess();
                close();

            }
        } catch (e) {
            onError(e);
            console.error(e);
        }
        finally {
            window.onbeforeunload = null;
            loader(false);
        }
    }

    const sendReceipt = async (transaction) => {

        let owner = folio.Owner;
        let folioTransactionId = transaction.Id;
        let receiptParams = [
            { key: "CurrencyId", value: currency },
            { key: "FolioId", value: folio && folio.Id },
            { key: "FolioTransactionId", value: folioTransactionId },
            { key: "LanguageId", value: owner.LanguageID }
        ]

        try{
            const receiptOriginal = await RenderTemplateToPdf({
                EntityTypeID: owner.EntityTypeId,
                FileName: "ReceiptOriginal",
                Paramters: [...receiptParams, { key: "ReceiptTitle", value: "ReceiptOriginal" }],
                TemplateTypeID: 17
            });
            const receiptOriginalSpec = {
                PdfDto: receiptOriginal,
                IsAttachBodyAsPdf: true,
                To: owner.Email,
                Subject: "ReceiptOriginal"
            }
            const receiptCopySpec = {
                PdfDto: receiptOriginal,
                IsAttachBodyAsPdf: true,
                To: owner.Email,
                Subject: "ReceiptCopy"
            }

            await SendEmail(receiptOriginalSpec)
            await SendEmail(receiptCopySpec)

        }catch(error){
            onError(error)
        }
    }


    const GenerateRequestFromSelectedPaymentMethod = () => {
        if(!reservation) return;

        const rv = {
            HotelId: reservation.HotelId,
            Currency: Currencies[currency].code,
            EntityId: reservation && reservation.Id,
            EntityTypeId: reservation && 1,
            FolioID: folio.Id,
            TotalCharge: amount,
            PaymentType: 1,
            Language: 3, //EN
            ExtEntity: folio.FolioEntities
                .filter(ee => ee.EntityTypeId !== EntityType.Reservation)
                .map(ee => { return { EntityId: ee.EntityId, EntityTypeId: ee.EntityTypeId } })
        }

        if (selectedPaymentMethod.external) {
            return {
                ...rv,
                CompanyPayment: selectedPaymentMethod.external.name,
                Track2: GetPinPadCode(selectedPaymentMethod.external.id),
                IsExternalPayment: true
            }
        } else if (selectedPaymentMethod.token) {
            const t = selectedPaymentMethod.token;
            // Read CVV from SimpleCvvInput if user entered it, otherwise fall back to token's stored CVV
            let cvv = t.Cvv;
            if (cvvInputRef.current) {
                const inputCvv = cvvInputRef.current.getCvv();
                if (inputCvv) {
                    cvv = inputCvv;
                }
            }
            return {
                ...rv,
                Token: t.Token,
                CompanyPayment: t.CompanyPaymentName,
                Cvv: cvv,
                CardExpDate: t.CreditCardExpirationDate,
            }
        }
    }

    const selectedToken = () => {
        if (selectedPaymentMethod && selectedPaymentMethod.token)
            return selectedPaymentMethod.token.Id;
        else
            return null;
    }

    const foliosSpec = useMemo(() => {
        if(!reservation) return;

        return {
            HotelID: reservation.HotelId,
            EntityIDs: [reservation.Id],
            EntityTypeIDs: [EntityType.Reservation],
            IncludeOwner: true,
            IncludeFolioEntities: true,
        }
    }, [reservation])

    const transactionSelected = (transactionCode) => {
        if(!reservation)    return;
        var transaction = {
            Id: 0,
            Amount: -amount,
            AmountBeforeTax: -amount,
            AmountAfterTax: -amount,
            CurrencyId: currency,
            FolioId: reservation?.Id,
            HotelID: reservation?.HotelID,
            IsVoid: false,
            TransactionDisplayName: transactionCode.Name,
            TransactionCodeId: transactionCode.Id,
            Quantity: 1
        }

        setSelectedPaymentMethod({ transaction: transaction })
        setPaymentTypeId(transactionCode.PaymentTypeID)
    }

    const currencySymbol = Currencies[currency]?.symbol
    const TransactionsSpec = useMemo(() => {
        return { IsDebit: false, IsHardcoded: false }
    }, [reservation])

    const filterTransactionCodes = (codes) => {
        // Filter city ledger transaction
        return codes.filter(x => ![6].includes(x.item.TransactionCodeHardcodedTypeID))
    }

    return (
        <Modal size={isMobileOnly ? "fullscreen" : "tiny"} open={open} className={isMobileOnly ? "mobile" : ""} style={{ zIndex: 1000000 }} {...rest} >
            {!isMobileOnly && <Modal.Header>{text("Receipt")}</Modal.Header>}
            <Modal.Content>
                <Form>
                    {reservation &&
                        <Form.Field
                            label={text("Folio")}
                            control={FoliosDropDown}
                            spec={foliosSpec}
                            selectFirst
                            onChangeItem={val => { setFolio(val) }} />
                    }
                    {/* </Form.Group> */}
                    <Form.Group widths="2">
                        <Form.Field
                            min={0}
                            label={text("Amount")}
                            value={amount}
                            onChange={handleAmountChange}
                            control={CurrencyInput}
                            currency={currencySymbol}
                            readOnly={!UserHasPermission(PermissionsActions.EditReceiptPrice)}>
                        </Form.Field>
                        <Form.Field label={text("Currency")} control={CurrencyDropDown} descriptionField="Symbol" value={currency} onChange={setCurrency}></Form.Field>
                    </Form.Group>
                    <Divider />
                    <Accordion defaultActiveIndex={0} panels={[
                        {
                            title: text("Credit"),
                            key: "credit",
                            content: {
                                content: <div>
                                    <Form.Field label={text("Transaction")} control={TransactionsDropDown} spec={TransactionsSpec}
                                        onChangeItem={item => transactionSelected(item)} filter={filterTransactionCodes} />
                                    {paymentTypeId === 6 && <Form.Group>
                                        <Form.Field width={10} label={text("Bank")} value={selectedPaymentMethod.ChecksDetails && selectedPaymentMethod.ChecksDetails.BankID} control={BanksDropDown} onChange={value => setSelectedPaymentMethod({
                                            ...selectedPaymentMethod,
                                            ChecksDetails: selectedPaymentMethod.ChecksDetails ? {
                                                ...selectedPaymentMethod.ChecksDetails,
                                                BankID: value
                                            } : {
                                                BankID: value
                                            }
                                        })} />
                                        <Form.Input width={6} label={text("BranchCode")} value={selectedPaymentMethod.ChecksDetails && selectedPaymentMethod.ChecksDetails.BranchCode} onChange={(e, { value }) => setSelectedPaymentMethod({
                                            ...selectedPaymentMethod,
                                            ChecksDetails: selectedPaymentMethod.ChecksDetails ? {
                                                ...selectedPaymentMethod.ChecksDetails,
                                                BranchCode: value
                                            } : {
                                                BranchCode: value
                                            }
                                        })}></Form.Input>
                                    </Form.Group>}
                                    {paymentTypeId === 6 && <Form.Group>
                                        <Form.Input width={5} label={text("CheckNumber")} value={selectedPaymentMethod.ChecksDetails && selectedPaymentMethod.ChecksDetails.CheckNumber} onChange={(e, { value }) => setSelectedPaymentMethod({
                                            ...selectedPaymentMethod,
                                            ChecksDetails: selectedPaymentMethod.ChecksDetails ? {
                                                ...selectedPaymentMethod.ChecksDetails,
                                                CheckNumber: value
                                            } : {
                                                CheckNumber: value
                                            }
                                        })}></Form.Input>
                                        <Form.Input width={5} label={text("AccountNumber")} value={selectedPaymentMethod.ChecksDetails && selectedPaymentMethod.ChecksDetails.AccountNumber} onChange={(e, { value }) => setSelectedPaymentMethod({
                                            ...selectedPaymentMethod,
                                            ChecksDetails: selectedPaymentMethod.ChecksDetails ? {
                                                ...selectedPaymentMethod.ChecksDetails,
                                                AccountNumber: value
                                            } : {
                                                AccountNumber: value
                                            }
                                        })}></Form.Input>
                                        <DatePicker width={6} label={text("CheckDate")} value={selectedPaymentMethod.ChecksDetails && selectedPaymentMethod.ChecksDetails.CheckDate} onChange={value => setSelectedPaymentMethod({
                                            ...selectedPaymentMethod,
                                            ChecksDetails: selectedPaymentMethod.ChecksDetails ? {
                                                ...selectedPaymentMethod.ChecksDetails,
                                                CheckDate: value
                                            } : {
                                                CheckDate: value
                                            }
                                        })} />
                                    </Form.Group>}
                                </div>
                            }
                        }, {
                            title: text("SavedCreditCards"),
                            key: "card",
                            content: {
                                content:
                                    <SavedCardsList
                                        reservation={reservation || {}}
                                        onSelectItem={value => setSelectedPaymentMethod({ token: value })}
                                        selected={selectedToken()}
                                        cvvRef={cvvInputRef} />
                            }
                        }, {
                            title: text("ExternalPayments"),
                            key: "emv",
                            content: {
                                content: <ExternalPayments onSelectItem={value => setSelectedPaymentMethod({
                                    external: value
                                })} />
                            }
                        }
                    ]} />
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button size={"small"} negative onClick={close}>{text("Close")}</Button>
                <Button disabled={!selectedPaymentMethod} size={"small"} positive onClick={save}>{text("Save")}</Button>
            </Modal.Actions>
        </Modal>
    )
}

ReceiptPopup.propTypes = {
    onClose: PropTypes.func,
    controller: PropTypes.string,
    startOpen: PropTypes.bool,
    reservation: PropTypes.object,
    isDefaultSendReceiptByMail: PropTypes.bool
}

export default ReceiptPopup
